import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react'

import { useField } from '@unform/core'
import { FiAlertCircle } from 'react-icons/fi'
import { Container, Error } from './styles'
import masks from '../../utils/inputMasks'

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string
  title: string
  placeholder?: string
  mask?: 'cep' | 'currency' | 'cpf' | 'phone' | 'date' | 'conta'
  sufix?: string
  smallText?: string
  color?: 'white' | 'gray'
  isDisabled?: boolean
}

const Input: React.FC<Props> = ({
  name,
  mask,
  sufix,
  placeholder,
  title,
  type,
  smallText,
  color = 'white',
  isDisabled = false,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isFilled, setIsFilled] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    fieldName,
    defaultValue,
    error,
    clearError,
    registerField,
  } = useField(name)

  useEffect(() => {
    if (type === 'date' && !!inputRef?.current?.value) {
      inputRef.current.type = type
    }
  }, [type])

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    })
  }, [fieldName, registerField])

  const handleInputFocus = useCallback(() => {
    setIsFocused(true)
    if (inputRef && inputRef.current && type === 'date') {
      inputRef.current.type = 'date'
    }
  }, [type])

  const handleInputBlur = useCallback(() => {
    setIsFocused(false)
    setIsFilled(!!inputRef.current?.value)

    if (type === 'date' && inputRef?.current?.value === '') {
      inputRef.current.type = 'text'
    }

    if (inputRef.current?.value) clearError()
  }, [clearError, type])

  const handleKeyUp = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      if (mask) masks({ type: mask, event })
    },
    [mask],
  )

  useEffect(() => {
    setIsFilled(!!inputRef.current?.value)
  }, [])

  return (
    <Container
      isErrored={!!error}
      isFilled={isFilled}
      isFocused={isFocused}
      hasSufix={!!sufix}
      color={color}
      isDisabled={isDisabled}
    >
      <label htmlFor={fieldName}>{title}</label>
      <div>
        <input
          type={type === 'date' ? 'text' : type}
          onKeyUp={handleKeyUp}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          defaultValue={defaultValue}
          ref={inputRef}
          disabled={isDisabled}
          placeholder={placeholder}
          name={name}
          {...rest}
        />
        {sufix && <span>{sufix}</span>}
        {error && (
          <Error title={error}>
            <FiAlertCircle size="20" />
          </Error>
        )}
      </div>
      {smallText && <small>{smallText}</small>}
    </Container>
  )
}

export default Input
